import { useState, useEffect } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import * as api from "../../fileUploaderAdminApi";
import ColumnCheckDrawer from "./ColumnCheckDrawer";

const RULE_TYPE_LABELS = {
  accepted_values: "Accepted Values",
  unique: "Unique",
};

/**
 * Audit view of all column validation rules across all templates.
 * Clicking edit opens the ColumnCheckDrawer inline and saves via the API.
 */
export default function ColumnChecksList() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingCheck, setEditingCheck] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [fetchingTemplate, setFetchingTemplate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      setChecks(await api.fetchColumnChecks(projectId));
    } catch (err) {
      setError(err.message || "Failed to load column checks.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleEditClick = async (cc) => {
    setFetchingTemplate(true);
    setSaveError("");
    try {
      const tmpl = await api.fetchTemplate(cc.template_id);
      setEditingCheck(cc);
      setEditingTemplate(tmpl);
      setDrawerOpen(true);
    } catch (err) {
      setError(err.message || "Failed to load template for editing.");
    } finally {
      setFetchingTemplate(false);
    }
  };

  const handleCheckSave = async ({ _editingId, column_name, ...rest }) => {
    if (!editingTemplate) return;
    setSaving(true);
    setSaveError("");

    const newCheck = { ...rest, _id: _editingId ?? `tmp-${Date.now()}` };
    const updatedColumns = editingTemplate.columns.map((col) => {
      if (col.name !== column_name) return col;
      if (_editingId) {
        return {
          ...col,
          column_checks: (col.column_checks ?? []).map((cc) =>
            cc._id === _editingId ? newCheck : cc
          ),
        };
      }
      return { ...col, column_checks: [...(col.column_checks ?? []), newCheck] };
    });

    try {
      await api.saveTemplate({
        isEdit: true,
        templateId: editingTemplate.id,
        form: {
          name: editingTemplate.name,
          description: editingTemplate.description,
          schema_name: editingTemplate.schema_name,
          schema_strict: editingTemplate.schema_strict,
          file_type_rows: editingTemplate.file_type_rows,
          storage_location_uri: editingTemplate.storage_location_uri,
          storage_location_type: editingTemplate.storage_location_type,
          dataset_name: editingTemplate.dataset_name,
          overwrite_existing: editingTemplate.overwrite_existing,
        },
        columns: updatedColumns,
        partnerId: editingTemplate.partner_id,
      });
      setDrawerOpen(false);
      setEditingCheck(null);
      setEditingTemplate(null);
      load();
    } catch (err) {
      setSaveError(err.message || "Failed to save column check.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {checks.length} rule{checks.length !== 1 ? "s" : ""} across all templates
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Edit a rule inline, or open the template to add new rules.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(`/projects/${projectId}/file-uploader/admin/templates/new`)}
        >
          New Template
        </Button>
      </Box>

      {fetchingTemplate && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      <Paper variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.50" }}>
              <TableCell>Template</TableCell>
              <TableCell>Schema</TableCell>
              <TableCell>Column</TableCell>
              <TableCell>Rule Type</TableCell>
              <TableCell>Applies To</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {checks.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No validation rules configured yet.
                </TableCell>
              </TableRow>
            )}
            {checks.map((cc) => (
              <TableRow key={cc._id} hover>
                <TableCell>
                  <Link
                    component={RouterLink}
                    to={`/projects/${projectId}/file-uploader/admin/templates/${cc.template_id}/edit`}
                    variant="body2"
                    underline="hover"
                  >
                    {cc.template_name}
                  </Link>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{cc.schema_name}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">{cc.column_name}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {RULE_TYPE_LABELS[cc.check_type] ?? cc.check_type}
                  </Typography>
                </TableCell>
                <TableCell>
                  {cc.user_emails.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">All users</Typography>
                  ) : (
                    <Stack spacing={0.25}>
                      {cc.user_emails.map((email) => (
                        <Typography key={email} variant="body2">{email}</Typography>
                      ))}
                    </Stack>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit rule">
                    <IconButton size="small" onClick={() => handleEditClick(cc)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Column check drawer — opens inline without leaving the tab */}
      <ColumnCheckDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditingCheck(null);
          setEditingTemplate(null);
        }}
        onSave={handleCheckSave}
        columns={editingTemplate?.columns ?? []}
        initialData={editingCheck}
        lockedColumnName={undefined}
        saving={saving}
        saveError={saveError}
      />
    </Box>
  );
}
